/**
 * หน้าจอแผนก — ครึ่ง "แผนก" ของหน้าจอ `DepartmentsEmployeesScreen` เดิม (`WO-P2-UI-001`)
 *
 * เดิมแผนกกับพนักงานอยู่หน้าเดียวกัน ทั้งที่เป็นงานคนละจังหวะ: ผู้ดูแลตั้งโครงแผนก
 * ทั้งองค์กรนาน ๆ ครั้ง ส่วนการเพิ่มพนักงานและผูกสังกัดเป็นงานประจำ การแยกออกเป็น
 * สองหน้าจึงไม่ได้เปลี่ยนอะไรในเชิงพฤติกรรม — **โค้ดถูกย้ายมาทั้งท่อน ไม่ได้เขียนใหม่**
 * และหน้านี้ยังเรียก client ตัวเดิม (`fetchDepartments`/`createDepartment`) ไม่มี
 * เส้นทาง API ใหม่แม้แต่เส้นเดียว
 *
 * ปิดสองเส้นทางของ contract: `GET /v1/admin/departments` และ
 * `POST /v1/admin/departments` (`WO-P1-M2-012`)
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับ `PermissionMatrixScreen`/`SystemRolesScreen`:
 * `RBAC_ADMIN` + `VIEW` เป็นสิทธิ์ของ **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน
 * `RBAC_ADMIN` + `EDIT` เป็นสิทธิ์ของ **ทุกปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้
 *
 * **เรื่องที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้:** แผนกที่เพิ่งสร้าง
 * **ยังไม่มีสิทธิ์ใดเลย** (deny-by-default) ผู้ดูแลที่ไม่รู้ข้อนี้จะสร้างแผนกแล้วบอก
 * พนักงานว่า "ใช้งานได้แล้ว" ซึ่งไม่จริงจนกว่าจะไปติ๊กช่องให้ที่ตารางสิทธิ์
 *
 * `WO-P2-UI-002` เปลี่ยนเฉพาะ **ที่อยู่ของฟอร์มสร้างแผนก** ไม่ได้เปลี่ยนสิ่งที่มันทำ:
 * แถบบนสุดเดิมเป็นฟอร์มที่แปะค้างไว้ตลอดเวลา ทั้งที่การตั้งโครงแผนกเป็นงานนาน ๆ ครั้ง
 * ส่วนงานที่ผู้ดูแลมาทำบ่อยคือ **หาแผนกสักแผนกในรายการ** ที่นั่นจึงกลายเป็นช่องค้นหา
 * และฟอร์มสร้างย้ายเข้าไปอยู่หลังปุ่ม "สร้างแผนก" ในกล่อง `Modal` — ฟิลด์เดิม
 * การตรวจเดิม คำขอเดิม (`createDepartment`) และหลังบันทึกสำเร็จก็ยัง **โหลดรายการใหม่
 * ด้วย `fetchDepartments`** ไม่ใช่การรีโหลดทั้งหน้า
 *
 * ช่องค้นหากรอง **ฝั่งเบราว์เซอร์จากรายการที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract
 * ของ `GET /v1/admin/departments` ไม่มีพารามิเตอร์ค้นหา และจำนวนแผนกทั้งองค์กรเป็น
 * หลักสิบ การกรองในหน่วยความจำจึงตรงไปตรงมากว่าและตอบทันทีที่พิมพ์
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ — ที่นี่สำคัญเป็นพิเศษ เพราะสิ่งที่หน้าจอนี้แก้คือตัวการ์ดเอง
 */

import { useCallback, useEffect, useState } from 'react';

import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import { toCreateDepartmentBody } from './departmentsView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createDepartment,
  fetchDepartments,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import { RBAC_ADMIN_MODULE, type Department } from '../../lib/rbacAdmin/types';

export interface DepartmentsScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type DepartmentsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly Department[] }
  | RbacAdminFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type PendingKind = 'create-department';

/**
 * กรองรายการแผนกด้วยคำค้นเดียว — เทียบทั้งชื่อและรหัสเพราะตารางแสดงทั้งสองคอลัมน์
 *
 * ผู้ดูแลที่คัดลอกรหัสแผนกมาจากหน้าตารางสิทธิ์ต้องหาแผนกนั้นเจอด้วยรหัสนั้น ไม่ใช่
 * ต้องแปลรหัสกลับเป็นชื่อก่อน
 */
function filterDepartments(
  items: readonly Department[],
  query: string,
): readonly Department[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (department) =>
      department.department_name.toLocaleLowerCase().includes(needle) ||
      department.department_id.toLocaleLowerCase().includes(needle),
  );
}

export function DepartmentsScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: DepartmentsScreenProps) {
  const canEdit = can(permissions, RBAC_ADMIN_MODULE, 'EDIT');

  const [departments, setDepartments] = useState<DepartmentsState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingKind | null>(null);

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [departmentName, setDepartmentName] = useState('');
  const [createdDepartment, setCreatedDepartment] = useState<Department | null>(null);
  const [departmentFailure, setDepartmentFailure] = useState<RbacAdminFailure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังบันทึกสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ดูแลเพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setDepartments((previous) =>
      previous.status === 'ready' ? previous : { status: 'loading' },
    );

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchDepartments(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setDepartments(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const openCreate = () => {
    if (!canEdit) {
      return;
    }
    // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (สำเร็จหรือถูกปฏิเสธ) ไม่ใช่ผลของครั้งนี้
    setDepartmentName('');
    setCreatedDepartment(null);
    setDepartmentFailure(null);
    setCreateOpen(true);
  };

  /**
   * อ้างอิงคงที่ — `Modal` ผูก/ถอน listener ของ Esc ตาม `onClose` ถ้าฟังก์ชันเปลี่ยน
   * ตัวทุกครั้งที่ผู้ใช้พิมพ์ชื่อแผนก listener จะถูกผูกใหม่ทุกตัวอักษรโดยไม่จำเป็น
   */
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const submitCreateDepartment = async () => {
    const body = toCreateDepartmentBody(departmentName);
    if (body === null || pending !== null || !canEdit) {
      return;
    }

    setPending('create-department');
    setCreatedDepartment(null);
    setDepartmentFailure(null);

    const result = await createDepartment(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่ผู้ดูแลเห็นยังเป็นสถานะที่เซิร์ฟเวอร์
      // ยืนยันล่าสุด ซึ่งสำหรับ `DEPARTMENT_NAME_TAKEN` คือ "แผนกชื่อนี้มีอยู่แล้วหนึ่ง
      // แผนก" และชื่อที่พิมพ์ไว้ยังอยู่ในช่องให้แก้แล้วส่งใหม่ได้ทันที
      setDepartmentFailure(result);
      return;
    }

    setCreatedDepartment(result.department);
    setDepartmentName('');
    setCreateOpen(false);
    // คำค้นที่ค้างอยู่ถูกล้างทิ้ง ไม่งั้นแผนกที่เพิ่งสร้าง (ซึ่งมักไม่ตรงกับคำค้นเดิม)
    // จะถูกกรองออกทันทีที่โหลดเสร็จ และผู้ดูแลจะเห็นคำว่า "สร้างแล้ว" คู่กับรายการที่
    // ไม่มีแผนกนั้นอยู่เลย
    setQuery('');
    // โหลดรายการใหม่ผ่าน `fetchDepartments` เส้นเดิม — แถวใหม่จึงโผล่มาเองโดยไม่ต้อง
    // รีโหลดทั้งหน้า (ไม่มี `window.location.reload` ที่ไหนในหน้านี้)
    setReloadToken((token) => token + 1);
  };

  const departmentItems = departments.status === 'ready' ? departments.items : [];
  const matchedItems = filterDepartments(departmentItems, query);
  const busy = pending !== null;

  return (
    <div className="cap-page" data-testid="rbac-departments">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            แผนก{' '}
            <InfoHint
              label="เกี่ยวกับหน้าแผนก"
              text={
                <>
                  แผนกคือสิ่งที่ <strong>ตารางสิทธิ์</strong> ผูกอยู่ — พนักงานได้สิทธิ์จากแผนกที่เขา
                  สังกัด ไม่ใช่จากตัวเขาเอง หน้าจอนี้คือที่ที่โครงแผนกทั้งองค์กรถูกตั้งขึ้น ส่วนการ
                  ผูกพนักงานเข้ากับแผนกอยู่ที่หน้า &quot;พนักงาน&quot;
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canEdit ? (
        <div className="cap-notice" data-testid="rbac-org-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังสร้างแผนกไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>{' '}
            ของโมดูล <code className="cap-table__code">{RBAC_ADMIN_MODULE}</code>{' '}
            ปุ่ม &quot;สร้างแผนก&quot; และฟอร์มที่อยู่หลังปุ่มนั้นจึงไม่แสดง — รายการและ
            ช่องค้นหายังใช้ได้ตามปกติ
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {/* ------------------------------------------------- ค้นหา + ปุ่มสร้าง -- */}

      <div className="cap-filters" data-testid="rbac-departments-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหาแผนก</span>
          <input
            className="cap-input"
            type="search"
            name="department_query"
            data-testid="rbac-department-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <InfoHint
            label="เกี่ยวกับช่องค้นหาแผนก"
            text="กรองรายการด้านล่างจากชื่อหรือรหัสแผนก — กรองจากรายการที่โหลดมาแล้ว ไม่ได้ถาม เซิร์ฟเวอร์ใหม่ ช่องว่างหมายถึงแสดงทุกแผนก"
          />
        </label>

        {canEdit ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="rbac-department-create-open"
            onClick={openCreate}
          >
            สร้างแผนก
          </button>
        ) : null}
      </div>

      {/* ---------------------------------------------------------- สร้างแผนก -- */}

      {/**
       * ฟอร์มเดิมทั้งท่อน แค่ย้ายมาอยู่ในกล่อง — ฟิลด์เดียวกัน การตรวจเดียวกัน
       * (`toCreateDepartmentBody`) คำขอเดียวกัน และ testid เดิมทุกตัวเพื่อให้สิ่งที่
       * เคยยืนยันไว้ยังยืนยันของเดิมอยู่
       */}
      <Modal open={canEdit && createOpen} onClose={closeCreate} title="สร้างแผนกใหม่">
        <form
          className="cap-form"
          data-testid="rbac-department-create-form"
          aria-label="สร้างแผนกใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreateDepartment();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">ชื่อแผนก</span>
            <input
              className="cap-input"
              name="department_name"
              data-testid="rbac-department-name"
              value={departmentName}
              autoComplete="off"
              onChange={(event) => {
                setDepartmentName(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับชื่อแผนก"
              text="ชื่อต้องไม่ซ้ำกันทั้งองค์กร — ชื่อที่ซ้ำจะถูกเซิร์ฟเวอร์ปฏิเสธด้วย 409"
            />
          </label>

          <p className="cap-field__hint" data-testid="rbac-department-create-hint">
            แผนกที่สร้างเสร็จจะ <strong>ยังไม่มีสิทธิ์ใดเลย</strong> (deny-by-default) —
            ต้องไปติ๊กช่องให้ที่หน้า &quot;ตารางสิทธิ์ตามแผนก&quot; อีกครั้ง
          </p>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="rbac-department-create-submit"
              disabled={busy || departmentName.trim() === ''}
            >
              {pending === 'create-department' ? 'กำลังสร้างแผนก…' : 'สร้างแผนก'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="rbac-department-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {/**
           * ความล้มเหลวอยู่ **ในกล่อง** ข้างฟอร์มที่ทำให้มันเกิด — ผู้ดูแลแก้ชื่อแล้วกด
           * ส่งใหม่ได้ทันทีโดยไม่ต้องเปิดกล่องใหม่และพิมพ์ใหม่ทั้งหมด
           */}
          {departmentFailure === null ? null : (
            <RbacAdminFailureNotice failure={departmentFailure} context="create-department" />
          )}
        </form>
      </Modal>

      {createdDepartment === null ? null : (
        <div className="cap-notice" data-testid="rbac-department-create-result" role="status">
          <StatusBadge tone="success">สร้างแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            สร้างแผนก {createdDepartment.department_name} แล้ว
          </h2>
          <p className="cap-notice__text">
            แผนกที่เพิ่งสร้าง <strong>ยังไม่มีสิทธิ์ใดเลย</strong> (deny-by-default) —
            ไปติ๊กช่องให้ที่หน้า &quot;ตารางสิทธิ์ตามแผนก&quot; ก่อน พนักงานที่สังกัดแผนกนี้จึง
            จะทำอะไรได้
          </p>
          <p className="cap-notice__note">
            รหัสแผนก:{' '}
            <code className="cap-table__code">{createdDepartment.department_id}</code>
          </p>
        </div>
      )}

      {/* --------------------------------------------------------- รายการแผนก -- */}

      {departments.status === 'loading' ? (
        <div className="cap-notice" data-testid="rbac-departments-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการแผนก…</p>
        </div>
      ) : null}

      {departments.status !== 'loading' && departments.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={departments}
          context="departments"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {departments.status === 'ready' && departmentItems.length === 0 ? (
        <div className="cap-notice" data-testid="rbac-departments-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีแผนก</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีแผนกในระบบเลย</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {/**
       * "หาไม่เจอ" ไม่ใช่ "ยังไม่มีแผนก" และไม่ใช่ "โหลดไม่สำเร็จ"
       *
       * ทั้งสามอย่างให้หน้าจอที่ไม่มีตารางเหมือนกัน แต่สิ่งที่ผู้ดูแลต้องทำต่อคนละเรื่อง
       * กันโดยสิ้นเชิง — อันนี้แก้ด้วยการลบคำค้น ไม่ใช่การสร้างแผนกหรือโทรหาไอที
       */}
      {departments.status === 'ready' &&
      departmentItems.length > 0 &&
      matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="rbac-departments-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบแผนกที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">ไม่มีแผนกที่ตรงกับ &quot;{query.trim()}&quot;</h2>
          <p className="cap-notice__text">
            องค์กรนี้มี {departmentItems.length} แผนก แต่ไม่มีแผนกใดที่ชื่อหรือรหัสมีคำนี้อยู่ —
            ล้างคำค้นเพื่อดูทั้งหมด
          </p>
        </div>
      ) : null}

      {departments.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="แผนกทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">แผนกทั้งหมด</h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามชื่อแผนกตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${departmentItems.length} แผนกที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="rbac-departments-table">
              <caption className="cap-visually-hidden">
                แผนกทั้งหมดขององค์กร พร้อมรหัสแผนกที่ใช้อ้างอิงในตารางสิทธิ์
              </caption>
              <thead>
                <tr>
                  <th scope="col">ชื่อแผนก</th>
                  <th scope="col">รหัสแผนก</th>
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((department) => (
                  <tr
                    key={department.department_id}
                    data-testid={`rbac-department-row-${department.department_id}`}
                  >
                    <th scope="row" className="cap-table__row-head">
                      {department.department_name}
                    </th>
                    <td>
                      <code className="cap-table__code">{department.department_id}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
